import { ReactNode } from "react";

import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { DocumentPreview } from "@shared";

import { CaseNoteResponse } from "@/api";
import { EllipsisText } from "@/common/components/ellipsis-text/EllipsisText";
import { HighlightedText } from "@/common/components/highlighted-text/HighlightedText";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { useCaseNoteLabel } from "@/common/hooks/useCaseNoteLabel";
import { formatCaseNoteDateTime } from "@/pages/clients/client/components/tabs/notes-tab/CaseNotes.utils";
import { AppointmentStatusChip } from "@/pages/clients/client/components/tabs/notes-tab/components/appointment-status-chip/AppointmentStatusChip";
import { useCaseNoteDocumentBlob } from "@/pages/clients/client/components/tabs/notes-tab/components/case-notes-overview/hooks/useCaseNoteDocumentBlob";
import { useVisibleAppointmentStatus } from "@/pages/clients/client/components/tabs/notes-tab/hooks/useVisibleAppointmentStatus";
import { useTranslation } from "@/translations";

interface Props {
  clientId: string;
  caseNote: CaseNoteResponse;
  headerAction?: ReactNode;
}

export function CaseNoteContent({ clientId, caseNote, headerAction }: Props) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const caseNoteLabel = useCaseNoteLabel(caseNote);
  const appointmentStatus = useVisibleAppointmentStatus(caseNote);

  // Populated only when the note was returned by a search, so non-search consumers
  // (e.g. the popout) render unhighlighted
  const highlightTerm = caseNote.searchHighlightTerm ?? "";

  const attachedDocument = caseNote.attachedDocument ?? null;
  const {
    blob: documentBlob,
    isLoading: isBlobLoading,
    errorKind,
  } = useCaseNoteDocumentBlob(clientId, attachedDocument?.documentId);

  function renderDocumentPreview() {
    if (isBlobLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
          <CircularProgress />
        </Box>
      );
    }
    if (errorKind === "notReady") {
      return <Typography>{t("clients.client.caseNotes.previewNotReady")}</Typography>;
    }
    if (errorKind === "failed") {
      return <Typography>{t("clients.client.caseNotes.previewFailed")}</Typography>;
    }
    if (errorKind) {
      return (
        <Typography>
          {t("clients.client.caseNotes.previewNotAvailable")}: {attachedDocument?.fileName}
        </Typography>
      );
    }
    if (documentBlob && attachedDocument) {
      return (
        <Box flex={1} minHeight={0}>
          <DocumentPreview blob={documentBlob} fileName={attachedDocument.fileName} />
        </Box>
      );
    }
    return null;
  }

  return (
    <Stack bgcolor={"white"} flex={1} p={2} gap={2}>
      <Box display={"flex"} justifyContent={"space-between"}>
        <Box flex={1} minWidth={0}>
          <EllipsisText value={caseNoteLabel} variant="h2">
            {attachedDocument ? (
              <HighlightedText text={attachedDocument.fileName} searchTerm={highlightTerm} />
            ) : undefined}
          </EllipsisText>
        </Box>
        <Box display={"flex"} gap={2} alignItems="center" flexShrink={0}>
          {appointmentStatus && <AppointmentStatusChip status={appointmentStatus} showStatusText />}
          <Typography variant="subtitle1">{formatCaseNoteDateTime(language, caseNote.date, caseNote.time)}</Typography>
          {headerAction}
        </Box>
      </Box>
      {attachedDocument ? (
        renderDocumentPreview()
      ) : (
        <Typography whiteSpace={"pre-wrap"}>
          <HighlightedText text={caseNote.content} searchTerm={highlightTerm} />
        </Typography>
      )}
      {caseNote.result && (
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            {t("clients.client.caseNotes.form.result")}
          </Typography>
          <Typography>
            <HighlightedText text={caseNote.result} searchTerm={highlightTerm} />
          </Typography>
        </Box>
      )}
    </Stack>
  );
}
